import React, {useCallback, useEffect, useState} from 'react';
import {Card} from "../card/card.jsx";
import InputForm from "../input/input-form.jsx";
import {MdSend} from "react-icons/md";
import axios from "axios";
import {useQuery} from "@tanstack/react-query";
import Button from "../button/button.jsx";
import ChatMessage from "./chat-message.jsx";
import "../../css/chat.css"
import ModalImage from "../modal/modal-image.jsx";
import {Modal} from "../modal/modal.jsx";
import ChatUtils from "../../utils/chat-utils.js";

let authHeaders;

const Chat = ({url, title = "Chat", onSendMessage, height = "h-full", renderMessages = true, callback, ref}) => {
    const [messages, setMessages] = useState([]);
    const [imageModal, setImageModal] = useState({open: false, imgSrc: undefined});
    const [videoModal, setVideoModal] = useState({open: false, videoSrc: undefined});
    const [inputMessage, setInputMessage] = useState("");

    const {data: credentials, isSuccess, isError, isLoading, refetch} = useQuery({
        queryFn: () => axios.get(url).then(({data}) => data),
        queryKey: ["chat", url]
    });

    useEffect(() => {
        if (!isSuccess) return;

        authHeaders = {'X-User-Id': credentials.login.userId, 'X-Auth-Token': credentials.login.authToken}
        const lastUpdate = '2023-01-01';

        axios.get(`${credentials.chatUrl}api/v1/chat.syncMessages?roomId=${credentials.group}&lastUpdate=${lastUpdate}`, {
            headers: authHeaders,
        })
            .then(response => {
                const messages = response.data.result.updated;

                if (callback) {
                    callback(messages);
                }

                if (renderMessages) {
                    setMessages(messages.reverse().filter(message => !message.t));

                    startWebSocket();
                }

            })
            .catch(error => {
                console.error('Failed to sync messages', error);

                const {status} = error.response;
                if (callback && [401].includes(status)) {
                    callback([], status)
                }
            });
    }, [isSuccess]);

    const startWebSocket = () => {
        if (ref.current) {
            ref.current?.close();
        }

        ref.current = new WebSocket(ChatUtils.getWebSocketUrl(credentials.chatUrl));

        ref.current.onopen = (event) => {
            event.target.send(JSON.stringify(getWebSocketConnectBody()));
        };

        ref.current.onmessage = (event) => {
            onWebSocketMessage(event);
        };
    }

    const getRandomId = () => {
        const min = Math.ceil(0);
        const max = Math.floor(100000);

        return Math.floor(Math.random() * (max - min) + min);
    }

    const getWebSocketConnectBody = () => {
        return {
            "msg": "connect",
            "version": "1",
            "support": ["1"]
        };
    }

    const getWebSocketPongBody = () => {
        return {
            "msg": "pong",
        }
    }

    const getWebSocketLoginBody = () => {
        return {
            "msg": "method",
            "method": "login",
            "id": `${getRandomId()}`,
            "params": [
                {
                    "resume": credentials.login.authToken
                }
            ]
        };
    }

    const getWebSocketSubscribeRoomMessagesBody = () => {
        return {
            "msg": "sub",
            "id": "uidstreamroommessages",
            "name": "stream-room-messages",
            "params": [
                credentials.group,
                {
                    "useCollection": false
                }
            ]
        }
    }

    const onWebSocketMessage = (event) => {
        const data = JSON.parse(event.data);

        if ("ping" === data.msg) {
            event.target.send(JSON.stringify(getWebSocketPongBody()));
            return;
        }

        if ("connected" === data.msg) {
            event.target.send(JSON.stringify(getWebSocketLoginBody()));
            return;
        }

        if ("added" === data.msg) {
            event.target.send(JSON.stringify(getWebSocketSubscribeRoomMessagesBody()));
        }

        if ("changed" === data.msg) {
            onMessageReceived(data.fields, callback);
        }
    }

    const onMessageReceived = useCallback((fields) => {
        if (credentials.group !== fields.eventName || !Array.isArray(fields.args) || fields.args.length === 0) {
            return;
        }

        const message = fields.args[0];
        if (message.t === null || message.t === undefined) setMessages(prev => [...prev, message])
    }, [messages, credentials]);

    const sendMessage = () => {
        const message = inputMessage.trim();

        if (message !== '') {
            const postBody = {
                "message": {
                    "rid": credentials.group,
                    "msg": message,
                }
            };

            const url = `${credentials.chatUrl}api/v1/chat.sendMessage`;

            axios.post(url, JSON.stringify(postBody), {
                headers: {
                    ...authHeaders,
                    'content-type': 'application/json',
                }
            })
            .then(() => {
                onSendMessage();
            })
            .catch(error => {
                console.error(error);
            });

            setInputMessage("");
        }
    }

    const handleKeyDown = ({key}) => {
        if (key === 'Enter')  sendMessage();
    }

    return (
        <>
            <Card className={height}>
                <Card.Header className={"!bg-white !justify-center rounded-t-[10px]"}>
                    <Card.Title className={"!bg-white"}>{title}</Card.Title>
                </Card.Header>
                <Card.Body className={"!p-0 " + (isSuccess ? "h-[calc(100%-93px)]" : "h-full")}>
                    {
                        isLoading &&
                        <div className={"h-full flex items-center justify-center gap-2"}>
                            <span className="inline-block size-5 rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] animate-spin text-gray-300"/>
                            Carregando chat…
                        </div>
                    }

                    {
                        isError &&
                        <div className={"h-full flex items-center flex-col justify-center gap-2"}>
                            <h5>Aviso</h5>
                            <p className="card-text">Não foi possível abrir o chat</p>
                            <Button onClick={() => refetch()}>
                                Tentar novamente
                            </Button>
                        </div>
                    }

                    {
                        isSuccess &&
                        <ul className={"chat-body"}>
                            {
                                messages.map((message) => (
                                    <ChatMessage
                                        key={message._id}
                                        message={message}
                                        setImageModal={setImageModal}
                                        setVideoModal={setVideoModal}
                                    />
                                ))
                            }
                        </ul>
                    }
                </Card.Body>
                {
                    isSuccess &&
                    <Card.Footer className={"!bg-white !justify-between w-full gap-2"}>
                        <InputForm
                            id={"chat-message"}
                            classNameWrapper={"w-full mt-2"}
                            label={"Mensagem"}
                            value={inputMessage}
                            onKeyDown={handleKeyDown}
                            onChange={(e) => setInputMessage(e.target.value)}
                        />

                        <Button
                            className={"flex rounded-md !h-auto !p-2 bg-[var(--blue-dark)] text-white items-center justify-center mt-2"}
                            onClick={sendMessage}>
                            <MdSend size={14}/>
                        </Button>
                    </Card.Footer>
                }
            </Card>

            <ModalImage
                open={imageModal.open}
                setOpen={() => setImageModal({open: false, imgSrc: undefined})}
                imgUrl={imageModal.imgSrc}
            />

            <Modal open={videoModal.open} setOpen={() => setVideoModal({open: false, videoSrc: undefined})}
                   title={"Vídeo"}>
                <Modal.Body className="pb-2 align-self-center justify-items-center max-h-[calc(100vh-200px)]">
                    <div className={"w-auto h-[calc(100vh-230px)]"}>
                        <video className={"video-component"} controls>
                            <source src={videoModal.videoSrc} type="video/mp4"/>
                        </video>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button target="primary" onClick={() => setVideoModal({open: false, videoSrc: undefined})}>
                        Ok
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Chat;